import type {
  LockerverseAuction as Auction,
  LockerverseAuctionItem as AuctionItem,
  LockerverseAuctionStatus,
} from "@lockerverse/sdk/auctions";
import { ChevronLeft } from "lucide-react";
import { type ReactNode, useEffect, useRef, useState } from "react";
import { AuctionDescription } from "./auction-description.tsx";
import { getAuctionDisplayStatus } from "./auction-display.ts";
import { LockerverseAuctionItemCard } from "./lockerverse-auction-item-card.tsx";
import type { LockerverseStyle, LockerverseTheme } from "./theme.ts";
import { useAuctionBoundary } from "./use-auction-boundary.ts";

export type LockerverseAuctionItemRenderContext = {
  auction: Auction;
  item: AuctionItem;
  /** Effective auction and item status, including elapsed closing dates. */
  status: LockerverseAuctionStatus;
  onSelect: () => void;
  /** Parent theme and Lockerverse CSS custom properties for default-card reuse. */
  theme: LockerverseTheme;
  style?: LockerverseStyle;
};
export type LockerverseAuctionProps = {
  auction: Auction;
  onSelectItem: (auction: Auction, item: AuctionItem) => void;
  theme?: LockerverseTheme;
  style?: LockerverseStyle;
  className?: string;
  showFilters?: boolean;
  showHeader?: boolean;
  showLineup?: boolean;
  onBack?: () => void;
  /** Replace the card inside each list item. Keep onSelect connected to your item view. */
  renderItem?: (context: LockerverseAuctionItemRenderContext) => ReactNode;
};
const FILTERS = [
  { label: "All", value: "all" },
  { label: "Live", value: "active" },
  { label: "Upcoming", value: "draft" },
  { label: "Closed", value: "finished" },
] as const;
type ItemFilter = (typeof FILTERS)[number]["value"];

/** Display one loaded auction. Fetching and item navigation stay with the host. */
export function LockerverseAuction({
  auction,
  onSelectItem,
  theme = "dark",
  style,
  className,
  showFilters = true,
  showHeader = true,
  showLineup = true,
  onBack,
  renderItem,
}: LockerverseAuctionProps) {
  return (
    <section
      aria-label={auction.name}
      className={[
        "lockerverse-auctions",
        "lockerverse-auctions--catalog",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
      data-lockerverse-theme={theme}
      style={style}
    >
      <AuctionContent
        auction={auction}
        onSelectItem={onSelectItem}
        showFilters={showFilters}
        showHeader={showHeader}
        showLineup={showLineup}
        theme={theme}
        {...(style ? { style } : {})}
        {...(onBack ? { onBack } : {})}
        {...(renderItem ? { renderItem } : {})}
      />
    </section>
  );
}

/** Shared content without a second themed container when used in the browser. */
export function AuctionContent({
  auction,
  onSelectItem,
  theme = "dark",
  style,
  showFilters = true,
  showHeader = true,
  showLineup = true,
  onBack,
  renderItem,
}: LockerverseAuctionProps) {
  useAuctionBoundary(auction.startAt, auction.endAt, auction.items);
  const [filterState, setFilterState] = useState<{
    auctionId: string;
    value: ItemFilter;
  }>({ auctionId: auction.id, value: "all" });
  const headingRef = useRef<HTMLHeadingElement>(null);
  const backRef = useRef<HTMLButtonElement>(null);
  const filter =
    showFilters && filterState.auctionId === auction.id
      ? filterState.value
      : "all";
  useEffect(() => {
    setFilterState({ auctionId: auction.id, value: "all" });
  }, [auction.id, showFilters]);
  const focusHeading = Boolean(onBack);
  useEffect(() => {
    if (focusHeading) {
      (headingRef.current ?? backRef.current)?.focus();
    }
  }, [auction.id, focusHeading, showHeader]);
  const cardStyle = style
    ? Object.fromEntries(
        Object.entries(style).filter(([key]) =>
          key.startsWith("--lockerverse-")
        )
      )
    : undefined;
  const items = [...auction.items].sort(
    (left, right) =>
      left.position - right.position ||
      left.createdAt.localeCompare(right.createdAt)
  );
  const filtered = items.filter(
    (item) =>
      filter === "all" || getAuctionDisplayStatus(auction, item) === filter
  );
  return (
    <>
      {onBack ? (
        <button
          className="lockerverse-auctions__catalog-back"
          onClick={onBack}
          ref={backRef}
          type="button"
        >
          <ChevronLeft aria-hidden="true" size={16} />
          Auctions
        </button>
      ) : null}
      {showHeader ? (
        <header className="lockerverse-auctions__catalog-header">
          <h2 ref={headingRef} tabIndex={-1}>
            {auction.name}
          </h2>
          {auction.description ? (
            <AuctionDescription description={auction.description} />
          ) : null}
        </header>
      ) : null}
      {showLineup ? (
        <div className="lockerverse-auctions__catalog-lineup">
          <div>
            <h3>Auction lineup</h3>
            <p>Browse the current collection.</p>
          </div>
          <span>
            {filtered.length} {filtered.length === 1 ? "item" : "items"}
          </span>
        </div>
      ) : null}
      {showFilters ? (
        <fieldset
          aria-label="Filter auction items"
          className="lockerverse-auctions__catalog-filters"
        >
          {FILTERS.map((option) => (
            <button
              aria-pressed={filter === option.value}
              key={option.value}
              onClick={() =>
                setFilterState({ auctionId: auction.id, value: option.value })
              }
              type="button"
            >
              {option.label}
              <span>
                {option.value === "all"
                  ? items.length
                  : items.filter(
                      (item) =>
                        getAuctionDisplayStatus(auction, item) === option.value
                    ).length}
              </span>
            </button>
          ))}
        </fieldset>
      ) : null}
      {filtered.length ? (
        <ul className="lockerverse-auctions__catalog-grid">
          {filtered.map((item) => {
            const context: LockerverseAuctionItemRenderContext = {
              auction,
              item,
              onSelect: () => onSelectItem(auction, item),
              status: getAuctionDisplayStatus(auction, item),
              theme,
            };
            if (cardStyle) {
              context.style = cardStyle;
            }
            return (
              <li key={item.id}>
                {renderItem ? (
                  renderItem(context)
                ) : (
                  <LockerverseAuctionItemCard {...context} />
                )}
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="lockerverse-auctions__catalog-empty">
          No items match this auction view.
        </p>
      )}
    </>
  );
}
